import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { FeatureAccessGate } from '@hopdrive/feature-portal-products';
import { useCustomerConfigLoaded, useRooftop, useUser } from '@store';
import { useFeaturePermissions, usePermissionsLoaded } from '@utils/featurePermissions';
import { isAdminUser } from '@features/userManagement/utils/roleUtils';
import { isRooftopLite } from '@utils/tiers';
import PermissionDeniedPage from './PermissionDeniedPage';
import AdminManageInAdminPortalPage from './AdminManageInAdminPortalPage';

interface ProtectedRouteProps {
  /** The page; absent for a path that only ever renders the rooftop gate's verdict. */
  children?: React.ReactNode;
  requiredFeature?: string | string[];
  /**
   * The rooftop has this page. Set, the route renders RESTRICTED ACCESS when it is false (once the
   * rooftop's config and products have settled), before any permission check. Unset, the route is
   * not rooftop-gated.
   */
  available?: boolean;
  /**
   * Block HopDrive Admin users from this route and send them to the Admin Portal
   * instead. Used for user/driver management, which is rooftop-scoped in the dealer
   * portal and the wrong surface for admins (who can see every customer).
   */
  blockAdmin?: boolean;
  /**
   * Restrict the route to a LITE rooftop. Full Service rooftops do not take part in the
   * offer lifecycle at all — HopDrive dispatches their work — so a route whose entire
   * subject is offering or assigning a driver has nothing to show them, and showing it
   * would invite an action the dispatch path refuses anyway.
   *
   * Gated on the ROOFTOP's tier, not on any one move: this is entitlement, and it has to
   * resolve the same way before any data loads.
   */
  requireLiteTier?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredFeature, blockAdmin, requireLiteTier, available }) => {
  const user = useUser();
  const rooftop = useRooftop();
  const customerConfigLoaded = useCustomerConfigLoaded();
  const location = useLocation();
  const permissionsLoaded = usePermissionsLoaded();
  const features = Array.isArray(requiredFeature)
    ? requiredFeature
    : requiredFeature
      ? [requiredFeature]
      : [];
  const permissionResults = useFeaturePermissions(features);

  // Check authentication first
  if (!user) {
    return <Navigate to='/login' state={{ from: location }} />;
  }

  // Admin users manage users/drivers in the Admin Portal, not the dealer portal.
  if (blockAdmin && isAdminUser(user.profile?.default_role)) {
    return <AdminManageInAdminPortalPage />;
  }

  // Tier entitlement, before the feature check: a Full Service rooftop is refused whatever
  // its feature grants say. `rooftop` is undefined until the selection resolves — render
  // nothing rather than flashing a denial at a lite user who is simply still loading, the
  // same wait `permissionsLoaded` gets below. Once the customer config has loaded (it
  // arrives with the rooftop, in the same fetch) a still-missing rooftop is not "loading",
  // it is "no rooftop selected" — an admin viewer, say — and a blank page forever would
  // read as a hang, so that case is refused like any other tier miss.
  if (requireLiteTier) {
    if (!rooftop) return customerConfigLoaded ? <PermissionDeniedPage /> : null;
    if (!isRooftopLite(rooftop)) return <PermissionDeniedPage />;
  }

  // The viewer's verdict: any required feature must be granted.
  const viewerPage = (() => {
    if (features.length === 0) return <>{children}</>;
    // Wait for permissions to load before checking feature access
    if (!permissionsLoaded) return null;
    // Check feature permission — any match grants access
    if (!permissionResults.some(Boolean)) return <PermissionDeniedPage />;
    return <>{children}</>;
  })();

  // The rooftop's verdict wraps the viewer's: a rooftop without the page reads RESTRICTED ACCESS
  // whatever this viewer's grants.
  if (available === undefined) return viewerPage;
  return (
    <FeatureAccessGate available={available} hostReady={customerConfigLoaded}>
      {viewerPage}
    </FeatureAccessGate>
  );
};

export default ProtectedRoute;
